import { Button, Theme, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { ProgressDots } from '@/components/DataDisplay/Indicator/ProgressDots';
import { Page } from '@/components/Layout/Page/Page';
import { PendingApproval } from '@/components/Layout/Page/PendingApproval';
import { PolicyFooter } from '@/components/Layout/PolicyFooter/PolicyFooter';

import { getAssetPath } from '@/constants/configs';
import { ROUTE } from '@/constants/routes';

import { useResponsive } from '@/hooks/useResponsive';
import { useTranslate } from '@/hooks/useTranslate';

import { OperationType } from '@/types/user';

import { getProgressSteps } from '@/utils/progressSteps';
import { censorEmail } from '@/utils/string';

import { VerifyEmailOtp } from './components/VerifyEmailOtp';
import { useEmailNotificationDrawer } from './hooks/useEmailNotificationDrawer';

import {
  EmailOTPDescription,
  EmailOTPTitle,
  PendingDescription,
  PendingTitle,
  PolicyFooterContainer,
} from './EmailOTP.styled';

export function EmailOTPPage() {
  const { t, language } = useTranslate();
  const theme = useTheme();

  const { state } = useLocation();
  const { form, type } = state ?? {};

  const navigate = useNavigate();
  const [isPendingProfile, setPendingProfile] = useState(false);
  const { isMobile } = useResponsive();

  useEmailNotificationDrawer(form?.countryCode);

  useEffect(() => {
    if (!form) {
      navigate(ROUTE.HOME, { replace: true });
    }
  }, [form, navigate]);

  if (!form) return null;

  const progressSteps = getProgressSteps();
  return (
    <Page
      paperProps={{
        color: (theme: Theme) => theme.palette.primaries[200],
        position: 'right',
        size: 'medium',
        ...(isPendingProfile
          ? {}
          : {
              fgImgProps: {
                src: getAssetPath(`/phone-verify-${language}.png`),
                overflowPercent: 50,
                width: 294,
              },
            }),
      }}
      hasLogo
      progressProps={progressSteps.emailOtpStep}
      showPendingApproval={!isMobile && isPendingProfile}
      hasBackToLoginButton={!isMobile && type !== OperationType.Remediation}
      headerMobile={{
        centerSlot: <ProgressDots {...progressSteps.emailOtpStep} />,
        bgColor: (theme: Theme) => theme.palette.background.paper,
      }}
    >
      {isPendingProfile ? (
        <>
          <PendingTitle>
            <Trans
              lang={language}
              i18nKey="login.pendingProfileModal.title"
              components={{
                a1: (
                  <Typography
                    component="span"
                    color={theme.palette.primaries[500]}
                  />
                ),
              }}
            />
          </PendingTitle>
          <PendingDescription>
            <Trans
              lang={language}
              i18nKey="login.pendingProfileModal.description"
            />
          </PendingDescription>
          {isMobile && (
            <>
              <PendingApproval />
              <Button
                fullWidth
                onClick={() => navigate(ROUTE.LOGIN, { replace: true })}
                variant="linkBold"
              >
                {t('createNewAccount.finished.button.backToLogin')}
              </Button>
            </>
          )}
        </>
      ) : (
        <>
          <VerifyEmailOtp
            countryForm={form}
            onPendingProfileChange={setPendingProfile}
            type={type}
          >
            <EmailOTPTitle>{t('login.emailOTP.verifyEmail')}</EmailOTPTitle>
            <EmailOTPDescription>
              <Trans
                lang={language}
                i18nKey="login.emailOTP.enterCode"
                values={{ email: censorEmail(form.email) }}
                components={{
                  br: <br />,
                  email: (
                    <Typography
                      variant={isMobile ? 'body2Bold' : 'body1Bold'}
                      color={theme.palette.text.primary}
                      component="span"
                    />
                  ),
                }}
              />
            </EmailOTPDescription>
          </VerifyEmailOtp>
          {type && type !== OperationType.New && (
            <PolicyFooterContainer>
              <PolicyFooter countryCode={form.countryCode} />
            </PolicyFooterContainer>
          )}
        </>
      )}
    </Page>
  );
}

export default EmailOTPPage;
